import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable, isDevMode } from '@angular/core';
import { Observable, catchError, forkJoin, map, throwError } from 'rxjs';

import { environment } from 'src/environments/environment';
import { FilenameConvention } from '../../models/filename-conventions/filename-convention';
import {
  FilenameConventionEvBased,
  FilenameConventionPayload,
  FilenameConventionRangeBased,
} from '../../models/filename-conventions/filename-convention-payload';
import {
  FilenameConventionCreatePayload,
  FilenameConventionUpdatePayload,
} from '../../models/filename-conventions/filename-convention-request-payload';

type Mode = FilenameConventionPayload['mode'];

interface RangeBackendEntity {
  id: string;
  organizationId: string;
  name: string;
  value: Omit<FilenameConventionRangeBased, 'mode'>;
  writeReason: FilenameConvention['writeReason'];
  createdAt: string;
}

interface EvBackendEntity {
  id: string;
  organizationId: string;
  name: string;
  value: Omit<FilenameConventionEvBased, 'mode'>;
  writeReason: FilenameConvention['writeReason'];
  createdAt: string;
}

interface RangeListResponse {
  rangeFilenameConventions?: RangeBackendEntity[];
}

interface EvListResponse {
  evFilenameConventions?: EvBackendEntity[];
}

interface RangeMutateResponse {
  rangeFilenameConvention: RangeBackendEntity;
}

interface EvMutateResponse {
  evFilenameConvention: EvBackendEntity;
}

interface RangeAuditResponse {
  revisions?: RangeBackendEntity[];
}

interface EvAuditResponse {
  revisions?: EvBackendEntity[];
}

interface RangeDeleteResponse {
  rangeFilenameConvention: RangeBackendEntity | null;
  warning: DependentsWarning | null;
}

interface EvDeleteResponse {
  evFilenameConvention: EvBackendEntity | null;
  warning: DependentsWarning | null;
}

interface DependentsWarning {
  count: number;
  names: string[];
}

export interface FilenameConventionDeleteResult {
  filenameConvention: FilenameConvention | null;
  warning: DependentsWarning | null;
}

@Injectable({
  providedIn: 'root',
})
export class FilenameConventionService {
  constructor(private http: HttpClient) {}

  list(organizationId: string): Observable<FilenameConvention[]> {
    if (isDevMode()) {
      console.log('[DEV] [FilenameConventionService] listing', organizationId);
    }
    const rangeUrl = this.collectionUrl(organizationId, 'rangeBased');
    const evUrl = this.collectionUrl(organizationId, 'evBased');
    const range$ = this.http
      .get<RangeListResponse>(rangeUrl)
      .pipe(map((res) => res.rangeFilenameConventions ?? []));
    const ev$ = this.http
      .get<EvListResponse>(evUrl)
      .pipe(map((res) => res.evFilenameConventions ?? []));
    return forkJoin([range$, ev$]).pipe(
      map(([range, ev]) => [
        ...range.map((entity) => this.reshapeRange(entity)),
        ...ev.map((entity) => this.reshapeEv(entity)),
      ]),
    );
  }

  getLatest(
    organizationId: string,
    id: string,
    mode?: Mode,
  ): Observable<FilenameConvention> {
    if (isDevMode()) {
      console.log('[DEV] [FilenameConventionService] loading latest', organizationId, id, { mode });
    }
    const rangeUrl = `${this.collectionUrl(organizationId, 'rangeBased')}/${id}/latest`;
    const evUrl = `${this.collectionUrl(organizationId, 'evBased')}/${id}/latest`;
    if (mode === 'rangeBased') {
      return this.http
        .get<RangeMutateResponse>(rangeUrl)
        .pipe(map((res) => this.reshapeRange(res.rangeFilenameConvention)));
    }
    if (mode === 'evBased') {
      return this.http
        .get<EvMutateResponse>(evUrl)
        .pipe(map((res) => this.reshapeEv(res.evFilenameConvention)));
    }
    return this.http.get<RangeMutateResponse>(rangeUrl).pipe(
      map((res) => this.reshapeRange(res.rangeFilenameConvention)),
      catchError((err: HttpErrorResponse) => {
        if (!this.isNotFound(err)) return throwError(() => err);
        return this.http
          .get<EvMutateResponse>(evUrl)
          .pipe(map((res) => this.reshapeEv(res.evFilenameConvention)));
      }),
    );
  }

  create(
    organizationId: string,
    payload: FilenameConventionCreatePayload,
  ): Observable<FilenameConvention> {
    if (isDevMode()) {
      console.log('[DEV] [FilenameConventionService] creating', organizationId, payload);
    }
    const mode = payload.value.payload.mode;
    const url = this.collectionUrl(organizationId, mode);
    const body = this.buildRequestBody(payload);
    if (mode === 'rangeBased') {
      return this.http
        .post<RangeMutateResponse>(url, body)
        .pipe(map((res) => this.reshapeRange(res.rangeFilenameConvention)));
    }
    return this.http
      .post<EvMutateResponse>(url, body)
      .pipe(map((res) => this.reshapeEv(res.evFilenameConvention)));
  }

  update(
    organizationId: string,
    id: string,
    payload: FilenameConventionUpdatePayload,
  ): Observable<FilenameConvention> {
    if (isDevMode()) {
      console.log('[DEV] [FilenameConventionService] updating', organizationId, id, payload);
    }
    const mode = payload.value.payload.mode;
    const url = `${this.collectionUrl(organizationId, mode)}/${id}`;
    const body = this.buildRequestBody(payload);
    if (mode === 'rangeBased') {
      return this.http
        .put<RangeMutateResponse>(url, body)
        .pipe(map((res) => this.reshapeRange(res.rangeFilenameConvention)));
    }
    return this.http
      .put<EvMutateResponse>(url, body)
      .pipe(map((res) => this.reshapeEv(res.evFilenameConvention)));
  }

  remove(
    organizationId: string,
    id: string,
    acknowledge: boolean = false,
    mode?: Mode,
  ): Observable<FilenameConventionDeleteResult> {
    if (isDevMode()) {
      console.log('[DEV] [FilenameConventionService] deleting', organizationId, id, {
        acknowledge,
        mode,
      });
    }
    const params = { acknowledge: String(acknowledge) };
    const rangeUrl = `${this.collectionUrl(organizationId, 'rangeBased')}/${id}`;
    const evUrl = `${this.collectionUrl(organizationId, 'evBased')}/${id}`;
    if (mode === 'rangeBased') {
      return this.http
        .delete<RangeDeleteResponse>(rangeUrl, { params })
        .pipe(map((res) => this.reshapeRangeDelete(res)));
    }
    if (mode === 'evBased') {
      return this.http
        .delete<EvDeleteResponse>(evUrl, { params })
        .pipe(map((res) => this.reshapeEvDelete(res)));
    }
    return this.http.delete<RangeDeleteResponse>(rangeUrl, { params }).pipe(
      map((res) => this.reshapeRangeDelete(res)),
      catchError((err: HttpErrorResponse) => {
        if (!this.isNotFound(err)) return throwError(() => err);
        return this.http
          .delete<EvDeleteResponse>(evUrl, { params })
          .pipe(map((res) => this.reshapeEvDelete(res)));
      }),
    );
  }

  getAudit(
    organizationId: string,
    id: string,
    mode?: Mode,
  ): Observable<FilenameConvention[]> {
    if (isDevMode()) {
      console.log('[DEV] [FilenameConventionService] loading audit', organizationId, id, { mode });
    }
    const rangeUrl = `${this.collectionUrl(organizationId, 'rangeBased')}/${id}/audit`;
    const evUrl = `${this.collectionUrl(organizationId, 'evBased')}/${id}/audit`;
    if (mode === 'rangeBased') {
      return this.http
        .get<RangeAuditResponse>(rangeUrl)
        .pipe(map((res) => (res.revisions ?? []).map((entity) => this.reshapeRange(entity))));
    }
    if (mode === 'evBased') {
      return this.http
        .get<EvAuditResponse>(evUrl)
        .pipe(map((res) => (res.revisions ?? []).map((entity) => this.reshapeEv(entity))));
    }
    return this.http.get<RangeAuditResponse>(rangeUrl).pipe(
      map((res) => (res.revisions ?? []).map((entity) => this.reshapeRange(entity))),
      catchError((err: HttpErrorResponse) => {
        if (!this.isNotFound(err)) return throwError(() => err);
        return this.http
          .get<EvAuditResponse>(evUrl)
          .pipe(map((res) => (res.revisions ?? []).map((entity) => this.reshapeEv(entity))));
      }),
    );
  }

  private collectionUrl(organizationId: string, mode: Mode): string {
    const slug = mode === 'rangeBased' ? 'range-filename-conventions' : 'ev-filename-conventions';
    return `${environment.apiBase}/organizations/${organizationId}/${slug}`;
  }

  private reshapeRange(entity: RangeBackendEntity): FilenameConvention {
    return {
      id: entity.id,
      organizationId: entity.organizationId,
      name: entity.name,
      value: {
        payload: {
          mode: 'rangeBased',
          entries: entity.value.entries,
        },
      },
      writeReason: entity.writeReason,
      createdAt: entity.createdAt,
    };
  }

  private reshapeEv(entity: EvBackendEntity): FilenameConvention {
    return {
      id: entity.id,
      organizationId: entity.organizationId,
      name: entity.name,
      value: {
        payload: {
          mode: 'evBased',
          template: entity.value.template,
          entries: entity.value.entries,
        },
      },
      writeReason: entity.writeReason,
      createdAt: entity.createdAt,
    };
  }

  private reshapeRangeDelete(res: RangeDeleteResponse): FilenameConventionDeleteResult {
    return {
      filenameConvention: res.rangeFilenameConvention
        ? this.reshapeRange(res.rangeFilenameConvention)
        : null,
      warning: res.warning,
    };
  }

  private reshapeEvDelete(res: EvDeleteResponse): FilenameConventionDeleteResult {
    return {
      filenameConvention: res.evFilenameConvention
        ? this.reshapeEv(res.evFilenameConvention)
        : null,
      warning: res.warning,
    };
  }

  private buildRequestBody(
    payload: FilenameConventionCreatePayload | FilenameConventionUpdatePayload,
  ): unknown {
    const valueWithoutMode = this.stripMode(payload.value.payload);
    return {
      name: payload.name,
      value: valueWithoutMode,
      ...(payload.writeReason === undefined ? {} : { writeReason: payload.writeReason }),
    };
  }

  private stripMode(payload: FilenameConventionPayload): Record<string, unknown> {
    const clone: Record<string, unknown> = { ...(payload as unknown as Record<string, unknown>) };
    delete clone['mode'];
    return clone;
  }

  private isNotFound(err: HttpErrorResponse): boolean {
    return err.status === 404;
  }
}
